import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {AddFundDialogProps} from '../../../../entity/props/marketplace/add-fund-dialog-props';
import {TokenPackageResponse} from '../../../../entity/resource/token-package-response';
import {useService} from '../../../../infrastructure/service/service';
import {User} from '../../../../entity/model/user';

import '../../../../assets/css/style.css';
import '../../../../assets/css/marketplace/add-fund-dialog.css';

const AddFundDialog = (props: AddFundDialogProps): JSX.Element => {
    const {subscriptionService, userService} = useService();

    const [tokenPackages, setTokenPackages] = useState<TokenPackageResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedPackage, setSelectedPackage] = useState<TokenPackageResponse | null>(null);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [currentUser, setCurrentUser] = useState<User>();

    useEffect(() => {
        const init = async () : Promise<void> => {
            setLoading(true);

            const user = await userService.getCurrentUser();
            setCurrentUser(user);

            const response = await subscriptionService.getTokenPackages();
            if (response && Array.isArray(response))
                setTokenPackages(response);

            setLoading(false);
        }

        init();
    }, []);

    const handlePackageSelect = (tokenPackage: TokenPackageResponse): void => {
        setSelectedPackage(tokenPackage);
    }

    const handlePurchase = async (): Promise<void> => {
        if (!selectedPackage) return;

        setSubmitLoading(true);

        const response = await subscriptionService.createCheckoutSession(currentUser?.organizationID ?? '', selectedPackage._id);
        if (response && response.checkoutURL)
            window.location.href = response.checkoutURL;
    }

    const handleClose = (): void => {
        setSelectedPackage(null);
        props.setOpen(false);
    }

    const formatPrice = (priceInCents: number): string => {
        return `$${(priceInCents / 100).toFixed(2)}`;
    }

    return (
        <Dialog open={props.open} className={'zitles-dialog add-funds-dialog'} maxWidth='md' fullWidth>
            <DialogTitle className={'zitles-dialog-title'}>
                <span>Add Funds</span>
                <IconButton onClick={handleClose}>
                    <CloseIcon className={'dialog-icon'}/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers className={'zitles-dialog-content'}>
                <Box className={'add-funds-header'}>
                    <Typography variant='h6' component='h4' className={'add-funds-title'}>Choose Token Package</Typography>
                    <Typography variant='body2' color='text.secondary' className={'add-funds-description'}>
                        Select a token package to add funds to your account.
                    </Typography>
                </Box>
                {
                    loading
                        ? <Box className={'add-funds-loading'}><CircularProgress/></Box>
                        : <Box className={'add-funds-packages'}>
                            {
                                tokenPackages.map((tokenPackage: TokenPackageResponse): JSX.Element => (
                                    <Card
                                        key={tokenPackage._id}
                                        className={`add-funds-package-card ${selectedPackage?._id === tokenPackage._id ? 'selected' : ''}`}
                                        onClick={() => handlePackageSelect(tokenPackage)}>
                                        <CardContent>
                                            <Box className={'add-funds-package-header'}>
                                                <Typography variant='h6' component='h3' className={'add-funds-package-name'}>{tokenPackage.name}</Typography>
                                                <Typography variant='h5' component='span' className={'add-funds-package-price'}>{formatPrice(tokenPackage.priceInCents)}</Typography>
                                            </Box>
                                            <Typography variant='body2' color='text.secondary' className={'add-funds-package-description'}>{tokenPackage.description}</Typography>
                                            <Box className={'add-funds-package-tokens'}>
                                                <Typography variant='body2' className={'add-funds-package-tokens-label'}>Tokens:</Typography>
                                                <Typography variant='body2' className={'add-funds-package-tokens-value'}>{tokenPackage.tokenAmount.toLocaleString()}</Typography>
                                            </Box>
                                        </CardContent>
                                        <CardActions className={'add-funds-package-actions'}>
                                            <Button
                                                variant={selectedPackage?._id === tokenPackage._id ? 'contained' : 'outlined'}
                                                fullWidth
                                                onClick={() => handlePackageSelect(tokenPackage)}
                                                className={selectedPackage?._id === tokenPackage._id ? 'zitles-button submit' : 'zitles-button cancel'}>
                                                {
                                                    selectedPackage?._id === tokenPackage._id
                                                        ? 'Selected'
                                                        : 'Select Package'
                                                }
                                            </Button>
                                        </CardActions>
                                    </Card>
                                ))
                            }
                        </Box>
                }
                <Divider className={'add-funds-divider'}/>
                <Box className={'add-funds-actions'}>
                    <Button onClick={handleClose} className={'zitles-button cancel'}>Cancel</Button>
                    <Button
                        onClick={handlePurchase}
                        disabled={!selectedPackage || submitLoading}
                        className={`zitles-button submit ${submitLoading ? 'disable' : ''}`}
                        startIcon={submitLoading ? <CircularProgress size={14} color='inherit' /> : null}>
                        {
                            submitLoading
                                ? 'Processing'
                                : 'Purchase Package'
                        }
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default observer(AddFundDialog);
